import { MediaModel as MediaApi } from '@/http/models/media/Media'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, Save } from 'lucide-react'

import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'

import { MediaFile } from '@/types/media'
import { formatSize } from '@/utils/formatSize'

interface ImageOptimizerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  file: MediaFile | null
  onOptimize: (fileId: string, settings: any) => void
}

export const ImageOptimizerDialog = ({
  open,
  onOpenChange,
  file,
  onOptimize,
}: ImageOptimizerDialogProps) => {
  const [quality, setQuality] = useState([80])
  const [format, setFormat] = useState('original')

  const optimizeMutation = useMutation({
    mutationFn: ({ id, quality, format }: { id: string; quality: number; format?: string }) =>
      MediaApi.optimizeImage({
        id,
        body: { quality, format: format === 'original' ? undefined : format },
      }),
    onSuccess: () => {
      onOptimize(file!.id, { quality: quality[0], format })
      onOpenChange(false)
    },
  })

  if (!file) return null

  const handleOptimize = () => {
    optimizeMutation.mutate({
      id: file.id,
      quality: quality[0],
      format: format === 'original' ? undefined : format,
    })
  }

  const originalSize = file.size
  const estimatedSize = Math.round(
    originalSize * (quality[0] / 100) * (format === 'webp' ? 0.7 : 1)
  )



  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Optimize image</DialogTitle>
          <DialogDescription>Reduce file size while maintaining visual quality.</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="flex items-center justify-center gap-6 rounded-lg border border-border-subtle bg-subdued p-4 sm:gap-8">
            <div className="text-center min-w-[80px]">
              <p className="mb-1 text-xs text-text-2">Original</p>
              <p className="text-heading-md tabular-nums text-ink">{formatSize(originalSize)}</p>
            </div>
            <div className="rounded-full border border-border bg-card p-1.5">
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="text-center min-w-[80px]">
              <p className="mb-1 text-xs text-text-2">Estimated</p>
              <p className="text-heading-md tabular-nums text-ink">~{formatSize(estimatedSize)}</p>
              <Badge tone="success" className="mt-1 tabular-nums">
                -{Math.round(((originalSize - estimatedSize) / originalSize) * 100)}%
              </Badge>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <Label>Compression quality</Label>
                <Badge tone="soft" className="tabular-nums">{quality[0]}%</Badge>
              </div>
              <Slider
                value={quality}
                onValueChange={setQuality}
                max={100}
                min={10}
                step={1}
                className="w-full"
                disabled={optimizeMutation.isPending}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Output format</Label>
              <Select
                value={format}
                onValueChange={setFormat}
                disabled={optimizeMutation.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="original">Keep original</SelectItem>
                  <SelectItem value="jpeg">JPEG</SelectItem>
                  <SelectItem value="png">PNG</SelectItem>
                  <SelectItem value="webp">WebP (best compression)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {optimizeMutation.isPending && (
            <div className="space-y-2">
              <div className="flex justify-between text-xs text-text-2">
                <span>Optimizing image…</span>
              </div>
              <Progress value={undefined} className="h-1.5 animate-pulse" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={optimizeMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleOptimize} disabled={optimizeMutation.isPending}>
            {optimizeMutation.isPending ? (
              <Save className="animate-pulse" />
            ) : (
              <Save />
            )}
            {optimizeMutation.isPending ? 'Processing…' : 'Save optimized copy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
